/**
 * Centralized HTTP client wrapper for Car Pulse Tracker (cpt-wui).
 *
 * A thin, typed wrapper around the native `fetch` API (no runtime dependency).
 * It centralizes the boilerplate that used to be duplicated across every call
 * site in `api.ts`:
 *   - request hook: builds default headers (Content-Type, selective-trace
 *     headers, and Bearer auth-token injection) in one place;
 *   - JSON (de)serialisation of request/response bodies;
 *   - uniform error shaping via a single typed `ApiError`;
 *   - a response hook for global error handling, notably sign-out on 401.
 *
 * The base URL is supplied by the caller (from `appConfig.apiBaseUrl`) — this
 * module never bakes in a default URL.
 */

import { sendErrorTelemetry, isTelemetryUrl } from './telemetry';

// ── Selective Tracing ─────────────────────────────────────────────
// Capture ?trace_secret= from the URL into sessionStorage on first load so the
// header can be propagated on every subsequent request.
const TRACE_STORAGE_KEY = 'cpt-trace-secret';
const AUTH_TOKEN_KEY = 'access_token';

function initTraceSecret(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const secret = params.get('trace_secret');
  if (secret) {
    sessionStorage.setItem(TRACE_STORAGE_KEY, secret);
    // Remove the param from the URL so it doesn't leak in bookmarks/shares
    params.delete('trace_secret');
    const clean = params.toString();
    const newUrl = window.location.pathname + (clean ? `?${clean}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }
}
initTraceSecret();

function getTraceHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const secret = sessionStorage.getItem(TRACE_STORAGE_KEY);
  if (!secret) return {};
  const headers: Record<string, string> = { 'X-Trace-Secret': secret };
  const sessionId = sessionStorage.getItem('cpt-sessionId') || localStorage.getItem('cpt-sessionId');
  if (sessionId) {
    try {
      headers['X-Session-ID'] = JSON.parse(sessionId);
    } catch {
      headers['X-Session-ID'] = sessionId;
    }
  }
  return headers;
}

function getAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

// ── Typed error ────────────────────────────────────────────────────

/**
 * Uniform error thrown by the HTTP client for any non-ok response.
 * Carries the HTTP status and the parsed response body (when available) so
 * callers can branch on either without re-parsing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    // Restore the prototype chain (required when targeting ES5/ES2015 classes).
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// ── Response hook: global 401 handling ─────────────────────────────

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Register a global handler invoked whenever a request returns 401.
 * Wired by the app entry point to clear session state and redirect/sign-out
 * via the Pinia store, keeping navigation out of the service layer.
 * Pass `null` to clear (primarily for tests).
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

// ── Request config ─────────────────────────────────────────────────

export type ResponseType = 'json' | 'blob';

export interface RequestConfig {
  /** Per-call header overrides, merged over the default headers. */
  headers?: Record<string, string>;
  /** Fallback message used when the error body has no usable `detail`. */
  errorMessage?: string;
  /**
   * When true (default), a JSON error body's `detail` field is preferred as the
   * thrown error message. Set false to always use `errorMessage` regardless of
   * the body (mirrors call sites that throw a fixed message).
   */
  useErrorDetail?: boolean;
  /** How to parse a successful response body. Defaults to 'json'. */
  responseType?: ResponseType;
  /**
   * When true, a 401 on this call does NOT invoke the global unauthorized
   * handler (sign-out). Use for secondary, in-wizard actions — file downloads
   * (receipt/report PDF) and their polling — where a transient auth failure
   * must surface as a local error rather than unloading the whole wizard. The
   * typed `ApiError` is still thrown so the caller can handle it locally.
   */
  skipUnauthorizedHandler?: boolean;
}

// ── Client ─────────────────────────────────────────────────────────

export class HttpClient {
  constructor(private readonly baseUrl: string) {}

  get<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('GET', path, undefined, config);
  }

  post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', path, body, config);
  }

  put<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('PUT', path, body, config);
  }

  delete<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('DELETE', path, undefined, config);
  }

  /** Build default headers (request hook): content-type, trace, auth token. */
  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getTraceHeaders(),
    };
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return { ...headers, ...extra };
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    config: RequestConfig = {}
  ): Promise<T> {
    const { headers, errorMessage, useErrorDetail = true, responseType = 'json', skipUnauthorizedHandler = false } = config;

    const init: RequestInit = {
      method,
      headers: this.buildHeaders(headers),
    };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    // Network failures (fetch rejections) propagate unchanged to the caller.
    const response = await fetch(`${this.baseUrl}${path}`, init);

    if (!response.ok) {
      return this.handleErrorResponse(response, errorMessage, useErrorDetail, skipUnauthorizedHandler);
    }

    if (responseType === 'blob') {
      return (await response.blob()) as T;
    }
    return (await response.json()) as T;
  }

  /** Response hook: shape a non-ok response into a typed ApiError. */
  private async handleErrorResponse(
    response: Response,
    fallbackMessage: string | undefined,
    useErrorDetail: boolean,
    skipUnauthorizedHandler = false
  ): Promise<never> {
    // Global 401 handling — clear session / sign-out via the registered hook.
    // Secondary in-wizard calls opt out (skipUnauthorizedHandler) so a stray
    // 401 surfaces as a local error instead of unloading the wizard.
    if (response.status === 401 && unauthorizedHandler && !skipUnauthorizedHandler) {
      try {
        unauthorizedHandler();
      } catch {
        // never let the sign-out hook mask the original error
      }
    }

    let parsedBody: unknown = null;
    try {
      parsedBody = await response.json();
    } catch {
      parsedBody = null;
    }

    // Report non-auth HTTP failures to backend telemetry (spec 037), skipping
    // the telemetry endpoint itself to avoid an infinite reporting loop.
    if (response.status !== 401 && !isTelemetryUrl(response.url)) {
      const detailText =
        parsedBody && typeof parsedBody === 'object' && 'detail' in parsedBody
          ? String((parsedBody as { detail?: unknown }).detail ?? '')
          : '';
      // Forward the server-issued distributed-trace ids (set by the API's
      // request-context middleware) so the client error report can be
      // correlated with the matching server-side trace. Absent headers are
      // simply omitted from the report.
      const traceId = response.headers.get('X-Trace-ID');
      const spanId = response.headers.get('X-Span-ID');
      sendErrorTelemetry({
        message: `HTTP ${response.status} ${response.url}${detailText ? `: ${detailText}` : ''}`,
        kind: 'http',
        trace_id: traceId ?? undefined,
        span_id: spanId ?? undefined,
      });
    }

    const fallback = fallbackMessage ?? `Request failed with status ${response.status}`;
    let message = fallback;
    if (
      useErrorDetail &&
      parsedBody &&
      typeof parsedBody === 'object' &&
      'detail' in parsedBody
    ) {
      const detail = (parsedBody as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail) message = detail;
    }

    throw new ApiError(message, response.status, parsedBody);
  }
}

/** Factory mirroring the class constructor for a functional call style. */
export function createHttpClient(baseUrl: string): HttpClient {
  return new HttpClient(baseUrl);
}
